package co.maytes.standupbot.dashboard.features.summary

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.features.analytics.DateRange
import co.maytes.standupbot.dashboard.features.analytics.ResourceState
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.shared.dto.summary.AccomplishmentSummaryDto
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone

sealed interface SummaryFocus {
    data object Overview : SummaryFocus
    data class Person(val userId: String, val displayName: String) : SummaryFocus
    data class Team(val teamId: String, val teamName: String) : SummaryFocus
}

class SummaryStore(private val api: ApiClient) {
    var overviewState: ResourceState<AccomplishmentSummaryDto> by mutableStateOf(ResourceState.Idle)
        private set

    var focusState: ResourceState<AccomplishmentSummaryDto> by mutableStateOf(ResourceState.Idle)
        private set

    var focus: SummaryFocus by mutableStateOf(SummaryFocus.Overview)
        private set

    private var currentWorkspaceId: String? = null
    private var currentRange: DateRange? = null

    private val cache = mutableMapOf<String, CacheEntry>()

    suspend fun load(workspaceId: String, range: DateRange) {
        currentWorkspaceId = workspaceId
        currentRange = range
        overviewState = fetch(workspaceId, range, focusUserId = null, focusTeamId = null)
        when (val f = focus) {
            is SummaryFocus.Person -> reloadFocus(workspaceId, range, focusUserId = f.userId, focusTeamId = null)
            is SummaryFocus.Team -> reloadFocus(workspaceId, range, focusUserId = null, focusTeamId = f.teamId)
            SummaryFocus.Overview -> focusState = ResourceState.Idle
        }
    }

    suspend fun setFocus(target: SummaryFocus) {
        focus = target
        val workspaceId = currentWorkspaceId ?: return
        val range = currentRange ?: return
        when (target) {
            is SummaryFocus.Person -> reloadFocus(workspaceId, range, focusUserId = target.userId, focusTeamId = null)
            is SummaryFocus.Team -> reloadFocus(workspaceId, range, focusUserId = null, focusTeamId = target.teamId)
            SummaryFocus.Overview -> focusState = ResourceState.Idle
        }
    }

    private suspend fun reloadFocus(workspaceId: String, range: DateRange, focusUserId: String?, focusTeamId: String?) {
        focusState = ResourceState.Loading
        focusState = fetch(workspaceId, range, focusUserId = focusUserId, focusTeamId = focusTeamId)
    }

    private suspend fun fetch(
        workspaceId: String,
        range: DateRange,
        focusUserId: String?,
        focusTeamId: String?,
    ): ResourceState<AccomplishmentSummaryDto> {
        val tz = TimeZone.currentSystemDefault().id
        val key = "$workspaceId:${range.from}:${range.to}:$tz:${focusUserId ?: ""}:${focusTeamId ?: ""}"
        cache[key]?.takeIf { it.fresh() }?.let { return ResourceState.Loaded(it.value) }
        val focusQuery = buildString {
            if (focusUserId != null) append("&focusUserId=$focusUserId")
            if (focusTeamId != null) append("&focusTeamId=$focusTeamId")
        }
        val path = "/api/v1/workspaces/$workspaceId/summary?from=${range.from}&to=${range.to}&tz=$tz$focusQuery"
        return when (val result = api.get<AccomplishmentSummaryDto>(path = path)) {
            is Result.Ok -> {
                cache[key] = CacheEntry(result.value, Clock.System.now().toEpochMilliseconds())
                ResourceState.Loaded(result.value)
            }
            is Result.Err -> ResourceState.Error(result.error)
        }
    }

    private data class CacheEntry(val value: AccomplishmentSummaryDto, val loadedAtMs: Long) {
        fun fresh(): Boolean = Clock.System.now().toEpochMilliseconds() - loadedAtMs < CACHE_TTL_MS
    }

    companion object {
        private const val CACHE_TTL_MS = 5L * 60L * 1000L
    }
}

@Composable
fun rememberSummaryStore(): SummaryStore {
    val api = rememberApiClient()
    return remember(api) { SummaryStore(api) }
}
